import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  Bell, 
  DollarSign, 
  Save,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Percent,
  Calculator,
  Package,
  Settings2,
  Zap,
  Clock,
  Mail,
  MessageSquare,
  Play,
  Pause,
  History,
  Target,
  Filter,
  Tag,
  Layers,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  XCircle,
  Info
} from "lucide-react";

const API = "/api";

// Kural Türleri
const RULE_TYPES = [
  { id: "stock_alert", label: "Stok Uyarısı", icon: AlertTriangle, color: "amber" },
  { id: "price_change", label: "Fiyat Değişikliği", icon: DollarSign, color: "emerald" },
  { id: "auto_discount", label: "Otomatik İndirim", icon: Percent, color: "blue" },
  { id: "reorder_point", label: "Yeniden Sipariş Noktası", icon: Package, color: "purple" },
  { id: "price_sync", label: "Fiyat Senkronizasyonu", icon: RefreshCw, color: "indigo" },
];

// Koşul Operatörleri
const OPERATORS = [
  { id: "lt", label: "küçükse (<)" },
  { id: "lte", label: "küçük eşitse (<=)" },
  { id: "gt", label: "büyükse (>)" },
  { id: "gte", label: "büyük eşitse (>=)" },
  { id: "eq", label: "eşitse (=)" },
  { id: "neq", label: "eşit değilse (!=)" },
];

// Aksiyon Türleri
const ACTION_TYPES = [
  { id: "email", label: "E-posta Gönder", icon: Mail },
  { id: "price_update", label: "Fiyat Güncelle", icon: DollarSign },
  { id: "discount_apply", label: "İndirim Uygula", icon: Percent },
  { id: "notification", label: "Bildirim Göster", icon: Bell },
  { id: "webhook", label: "Webhook Tetikle", icon: Zap },
  { id: "sync_shopify", label: "Shopify Senkronize Et", icon: RefreshCw },
];

export default function AdvancedRulesPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Rules
  const [rules, setRules] = useState([]);
  const [ruleLogs, setRuleLogs] = useState([]);
  
  // Stats
  const [stats, setStats] = useState({
    totalRules: 0,
    activeRules: 0,
    triggeredToday: 0,
    lowStockCount: 0
  });
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  
  // Form
  const [ruleForm, setRuleForm] = useState({
    name: "",
    description: "",
    type: "stock_alert",
    conditions: [
      { field: "stock", operator: "lt", value: 10 }
    ],
    actions: [
      { type: "notification", config: {} }
    ],
    schedule: {
      type: "realtime", // realtime, interval, cron
      intervalMinutes: 60,
      cronExpression: ""
    },
    filters: {
      categories: [],
      brands: [],
      products: [],
      warehouses: []
    },
    isActive: true,
    priority: 1
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rulesRes, logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/advanced-rules?token=${token}`),
        axios.get(`${API}/advanced-rules/logs?token=${token}&limit=20`),
        axios.get(`${API}/advanced-rules/stats?token=${token}`)
      ]);
      
      setRules(rulesRes.data.rules || []);
      setRuleLogs(logsRes.data.logs || []);
      setStats(statsRes.data || {
        totalRules: rulesRes.data.rules?.length || 0,
        activeRules: rulesRes.data.rules?.filter(r => r.isActive).length || 0,
        triggeredToday: logsRes.data.logs?.length || 0,
        lowStockCount: 0
      });
    } catch (error) {
      console.error("Rules fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingRule(null);
    setRuleForm({
      name: "",
      description: "",
      type: "stock_alert",
      conditions: [
        { field: "stock", operator: "lt", value: 10 }
      ],
      actions: [
        { type: "notification", config: {} }
      ],
      schedule: {
        type: "realtime",
        intervalMinutes: 60,
        cronExpression: ""
      },
      filters: {
        categories: [],
        brands: [],
        products: [],
        warehouses: []
      },
      isActive: true,
      priority: 1
    });
    setDialogOpen(true);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name || "",
      description: rule.description || "",
      type: rule.type || "stock_alert",
      conditions: rule.conditions || [{ field: "stock", operator: "lt", value: 10 }],
      actions: rule.actions || [{ type: "notification", config: {} }],
      schedule: rule.schedule || { type: "realtime", intervalMinutes: 60, cronExpression: "" },
      filters: rule.filters || { categories: [], brands: [], products: [], warehouses: [] },
      isActive: rule.isActive !== false,
      priority: rule.priority || 1
    });
    setDialogOpen(true);
  };

  const saveRule = async () => {
    if (!ruleForm.name) {
      toast.error("Kural adı zorunludur");
      return;
    }

    setSaving(true);
    try {
      if (editingRule) {
        await axios.put(`${API}/advanced-rules/${editingRule.id}?token=${token}`, ruleForm);
        toast.success("Kural güncellendi");
      } else {
        await axios.post(`${API}/advanced-rules?token=${token}`, ruleForm);
        toast.success("Kural oluşturuldu");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!confirm("Bu kuralı silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/advanced-rules/${ruleId}?token=${token}`);
      toast.success("Kural silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const toggleRule = async (ruleId, isActive) => {
    try {
      await axios.put(`${API}/advanced-rules/${ruleId}/toggle?token=${token}`, { isActive });
      toast.success(isActive ? "Kural aktifleştirildi" : "Kural devre dışı bırakıldı");
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const runRuleNow = async (ruleId) => {
    try {
      const response = await axios.post(`${API}/advanced-rules/${ruleId}/run?token=${token}`);
      toast.success(`Kural çalıştırıldı: ${response.data.matched || 0} eşleşme bulundu`);
      fetchData();
    } catch (error) {
      toast.error("Kural çalıştırılamadı");
    }
  };

  const addCondition = () => {
    setRuleForm({
      ...ruleForm,
      conditions: [...ruleForm.conditions, { field: "stock", operator: "lt", value: 0 }]
    });
  };

  const removeCondition = (index) => {
    setRuleForm({
      ...ruleForm,
      conditions: ruleForm.conditions.filter((_, i) => i !== index)
    });
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...ruleForm.conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setRuleForm({ ...ruleForm, conditions: newConditions });
  };

  const addAction = () => {
    setRuleForm({
      ...ruleForm,
      actions: [...ruleForm.actions, { type: "notification", config: {} }]
    });
  };

  const removeAction = (index) => {
    setRuleForm({
      ...ruleForm,
      actions: ruleForm.actions.filter((_, i) => i !== index)
    });
  };

  const updateAction = (index, field, value) => {
    const newActions = [...ruleForm.actions];
    newActions[index] = { ...newActions[index], [field]: value };
    setRuleForm({ ...ruleForm, actions: newActions });
  };

  const getRuleTypeInfo = (type) => {
    return RULE_TYPES.find(t => t.id === type) || RULE_TYPES[0];
  };

  const getActionTypeInfo = (type) => {
    return ACTION_TYPES.find(t => t.id === type) || ACTION_TYPES[0];
  };

  return (
    <div className="space-y-6 fade-in" data-testid="advanced-rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gelişmiş Kurallar Motoru</h1>
          <p className="text-slate-500 mt-1">Otomatik stok uyarıları ve dinamik fiyat kuralları</p>
        </div>
        <Button onClick={openAddDialog} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kural
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Settings2 className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalRules}</p>
                <p className="text-xs text-slate-500">Toplam Kural</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeRules}</p>
                <p className="text-xs text-slate-500">Aktif Kural</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Zap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.triggeredToday}</p>
                <p className="text-xs text-slate-500">Bugün Tetiklenen</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.lowStockCount}</p>
                <p className="text-xs text-slate-500">Düşük Stok Ürün</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="rules" className="space-y-6">
        <TabsList>
          <TabsTrigger value="rules" className="flex items-center gap-2">
            <Settings2 className="w-4 h-4" />
            Kurallar
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Loglar
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Şablonlar
          </TabsTrigger>
        </TabsList>

        {/* Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Aktif Kurallar</CardTitle>
              <CardDescription>Stok ve fiyat yönetimi için otomatik kurallar</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : rules.length === 0 ? (
                <div className="text-center py-12">
                  <Settings2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Henüz kural oluşturulmadı</p>
                  <Button onClick={openAddDialog} className="mt-4" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    İlk Kuralı Oluştur
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kural Adı</TableHead>
                      <TableHead>Tür</TableHead>
                      <TableHead>Koşullar</TableHead>
                      <TableHead>Aksiyonlar</TableHead>
                      <TableHead className="text-center">Zamanlama</TableHead>
                      <TableHead className="text-center">Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rules.map((rule) => {
                      const typeInfo = getRuleTypeInfo(rule.type);
                      const TypeIcon = typeInfo.icon;
                      return (
                        <TableRow key={rule.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{rule.name}</p>
                              <p className="text-xs text-slate-500 truncate max-w-[200px]">{rule.description}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`bg-${typeInfo.color}-500 flex items-center gap-1 w-fit`}>
                              <TypeIcon className="w-3 h-3" />
                              {typeInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{rule.conditions?.length || 0} koşul</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{rule.actions?.length || 0} aksiyon</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={
                              rule.schedule?.type === "realtime" ? "bg-emerald-100 text-emerald-700" :
                              rule.schedule?.type === "interval" ? "bg-blue-100 text-blue-700" :
                              "bg-purple-100 text-purple-700"
                            }>
                              {rule.schedule?.type === "realtime" ? "Anlık" :
                               rule.schedule?.type === "interval" ? `${rule.schedule.intervalMinutes}dk` :
                               "Zamanlanmış"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={rule.isActive}
                              onCheckedChange={(checked) => toggleRule(rule.id, checked)}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="icon" variant="ghost" onClick={() => runRuleNow(rule.id)} title="Şimdi Çalıştır">
                                <Play className="w-4 h-4 text-emerald-500" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => openEditDialog(rule)}>
                                <Pencil className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => deleteRule(rule.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-slate-500" />
                Kural Çalışma Logları
              </CardTitle>
              <CardDescription>Son kural tetiklenmeleri ve sonuçları</CardDescription>
            </CardHeader>
            <CardContent>
              {ruleLogs.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Henüz log kaydı yok</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Kural</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Eşleşme</TableHead>
                      <TableHead>Mesaj</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ruleLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          {new Date(log.created_at).toLocaleString("tr-TR")}
                        </TableCell>
                        <TableCell className="font-medium">{log.ruleName}</TableCell>
                        <TableCell>
                          {log.status === "success" ? (
                            <Badge className="bg-emerald-500">Başarılı</Badge>
                          ) : log.status === "error" ? (
                            <Badge className="bg-red-500">Hata</Badge>
                          ) : (
                            <Badge className="bg-amber-500">Kısmi</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.matchedCount || 0} ürün</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[300px] truncate">
                          {log.message}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Stok Uyarı Şablonu */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
              setRuleForm({
                name: "Düşük Stok Uyarısı",
                description: "Stok 10'un altına düştüğünde bildirim gönder",
                type: "stock_alert",
                conditions: [{ field: "stock", operator: "lt", value: 10 }],
                actions: [{ type: "notification", config: { title: "Düşük Stok", message: "Ürün stoğu kritik seviyede" }}],
                schedule: { type: "realtime", intervalMinutes: 60, cronExpression: "" },
                filters: { categories: [], brands: [], products: [], warehouses: [] },
                isActive: true,
                priority: 1
              });
              setEditingRule(null);
              setDialogOpen(true);
            }}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Düşük Stok Uyarısı</h4>
                    <p className="text-sm text-slate-500 mt-1">Stok belirli seviyenin altına düştüğünde uyarı ver</p>
                    <Badge className="mt-2 bg-amber-500">Stok Uyarısı</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Kritik Stok Şablonu */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
              setRuleForm({
                name: "Kritik Stok - Acil",
                description: "Stok 2'nin altına düştüğünde e-posta gönder",
                type: "stock_alert",
                conditions: [{ field: "stock", operator: "lte", value: 2 }],
                actions: [{ type: "email", config: { subject: "ACİL: Kritik Stok Seviyesi", template: "critical_stock" }}],
                schedule: { type: "realtime", intervalMinutes: 60, cronExpression: "" },
                filters: { categories: [], brands: [], products: [], warehouses: [] },
                isActive: true,
                priority: 1
              });
              setEditingRule(null);
              setDialogOpen(true);
            }}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Kritik Stok - Acil</h4>
                    <p className="text-sm text-slate-500 mt-1">Kritik seviyede e-posta bildirimi gönder</p>
                    <Badge className="mt-2 bg-red-500">Acil Uyarı</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dinamik Fiyatlandırma */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
              setRuleForm({
                name: "Kar Marjı Kontrolü",
                description: "Fiyat = Maliyet × 1.5 (kar marjı %50)",
                type: "price_change",
                conditions: [{ field: "margin", operator: "lt", value: 30 }],
                actions: [{ type: "price_update", config: { formula: "cost * 1.5" }}],
                schedule: { type: "interval", intervalMinutes: 60, cronExpression: "" },
                filters: { categories: [], brands: [], products: [], warehouses: [] },
                isActive: true,
                priority: 2
              });
              setEditingRule(null);
              setDialogOpen(true);
            }}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Kar Marjı Kontrolü</h4>
                    <p className="text-sm text-slate-500 mt-1">Minimum kar marjı altındaki ürünleri güncelle</p>
                    <Badge className="mt-2 bg-emerald-500">Fiyat</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Otomatik İndirim */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
              setRuleForm({
                name: "Yaşlanan Stok İndirimi",
                description: "90 günden eski stoklara %20 indirim",
                type: "auto_discount",
                conditions: [{ field: "stock_age_days", operator: "gt", value: 90 }],
                actions: [{ type: "discount_apply", config: { percent: 20, reason: "Sezon Sonu" }}],
                schedule: { type: "interval", intervalMinutes: 1440, cronExpression: "" },
                filters: { categories: [], brands: [], products: [], warehouses: [] },
                isActive: true,
                priority: 3
              });
              setEditingRule(null);
              setDialogOpen(true);
            }}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Percent className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Yaşlanan Stok İndirimi</h4>
                    <p className="text-sm text-slate-500 mt-1">Eski stoklara otomatik indirim uygula</p>
                    <Badge className="mt-2 bg-blue-500">İndirim</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Yeniden Sipariş */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
              setRuleForm({
                name: "Yeniden Sipariş Noktası",
                description: "Stok minimum seviyeye düştüğünde tedarikçiye bildir",
                type: "reorder_point",
                conditions: [{ field: "stock", operator: "lte", value: 5 }],
                actions: [
                  { type: "notification", config: { title: "Sipariş Gerekli" }},
                  { type: "email", config: { subject: "Yeniden Sipariş Gerekli" }}
                ],
                schedule: { type: "interval", intervalMinutes: 60, cronExpression: "" },
                filters: { categories: [], brands: [], products: [], warehouses: [] },
                isActive: true,
                priority: 2
              });
              setEditingRule(null);
              setDialogOpen(true);
            }}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Package className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Yeniden Sipariş Noktası</h4>
                    <p className="text-sm text-slate-500 mt-1">Min stok seviyesinde tedarikçi bildirimi</p>
                    <Badge className="mt-2 bg-purple-500">Tedarik</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Shopify Sync */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
              setRuleForm({
                name: "Shopify Fiyat Senkronizasyonu",
                description: "Her saat başı fiyatları Shopify'a senkronize et",
                type: "price_sync",
                conditions: [{ field: "price_updated", operator: "eq", value: true }],
                actions: [{ type: "sync_shopify", config: { fields: ["price", "compare_at_price"] }}],
                schedule: { type: "interval", intervalMinutes: 60, cronExpression: "" },
                filters: { categories: [], brands: [], products: [], warehouses: [] },
                isActive: true,
                priority: 2
              });
              setEditingRule(null);
              setDialogOpen(true);
            }}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <RefreshCw className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Shopify Senkronizasyonu</h4>
                    <p className="text-sm text-slate-500 mt-1">Fiyat değişikliklerini otomatik Shopify'a aktar</p>
                    <Badge className="mt-2 bg-indigo-500">Senkronizasyon</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Rule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRule ? "Kuralı Düzenle" : "Yeni Kural Oluştur"}</DialogTitle>
            <DialogDescription>Stok ve fiyat için otomatik kural tanımlayın</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Temel Bilgiler */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ruleName">Kural Adı *</Label>
                <Input
                  id="ruleName"
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm({...ruleForm, name: e.target.value})}
                  placeholder="Örn: Düşük Stok Uyarısı"
                />
              </div>
              <div className="space-y-2">
                <Label>Kural Türü</Label>
                <Select 
                  value={ruleForm.type} 
                  onValueChange={(v) => setRuleForm({...ruleForm, type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_TYPES.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                value={ruleForm.description}
                onChange={(e) => setRuleForm({...ruleForm, description: e.target.value})}
                placeholder="Bu kural ne yapıyor?"
                rows={2}
              />
            </div>

            {/* Koşullar */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Koşullar</Label>
                <Button size="sm" variant="outline" onClick={addCondition}>
                  <Plus className="w-4 h-4 mr-1" />
                  Koşul Ekle
                </Button>
              </div>
              
              <div className="space-y-2">
                {ruleForm.conditions.map((condition, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <Select 
                      value={condition.field} 
                      onValueChange={(v) => updateCondition(idx, "field", v)}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stock">Stok</SelectItem>
                        <SelectItem value="price">Fiyat</SelectItem>
                        <SelectItem value="cost">Maliyet</SelectItem>
                        <SelectItem value="margin">Kar Marjı %</SelectItem>
                        <SelectItem value="stock_age_days">Stok Yaşı (gün)</SelectItem>
                        <SelectItem value="sales_velocity">Satış Hızı</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select 
                      value={condition.operator} 
                      onValueChange={(v) => updateCondition(idx, "operator", v)}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPERATORS.map((op) => (
                          <SelectItem key={op.id} value={op.id}>{op.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Input
                      type="number"
                      value={condition.value}
                      onChange={(e) => updateCondition(idx, "value", parseFloat(e.target.value) || 0)}
                      className="w-[100px]"
                    />
                    
                    {ruleForm.conditions.length > 1 && (
                      <Button size="icon" variant="ghost" onClick={() => removeCondition(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Aksiyonlar */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Aksiyonlar</Label>
                <Button size="sm" variant="outline" onClick={addAction}>
                  <Plus className="w-4 h-4 mr-1" />
                  Aksiyon Ekle
                </Button>
              </div>
              
              <div className="space-y-2">
                {ruleForm.actions.map((action, idx) => {
                  const actionInfo = getActionTypeInfo(action.type);
                  const ActionIcon = actionInfo.icon;
                  return (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                      <Select 
                        value={action.type} 
                        onValueChange={(v) => updateAction(idx, "type", v)}
                      >
                        <SelectTrigger className="w-[200px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACTION_TYPES.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              <div className="flex items-center gap-2">
                                <type.icon className="w-4 h-4" />
                                {type.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <div className="flex-1 text-sm text-slate-500">
                        <ActionIcon className="w-4 h-4 inline mr-1" />
                        {actionInfo.label}
                      </div>
                      
                      {ruleForm.actions.length > 1 && (
                        <Button size="icon" variant="ghost" onClick={() => removeAction(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Zamanlama */}
            <div className="space-y-3">
              <Label>Zamanlama</Label>
              <div className="grid grid-cols-3 gap-4">
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    ruleForm.schedule.type === "realtime" ? "border-indigo-500 bg-indigo-50" : "border-slate-200"
                  }`}
                  onClick={() => setRuleForm({...ruleForm, schedule: {...ruleForm.schedule, type: "realtime"}})}
                >
                  <Zap className="w-5 h-5 text-indigo-600 mb-2" />
                  <p className="font-medium">Anlık</p>
                  <p className="text-xs text-slate-500">Her değişiklikte çalış</p>
                </div>
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    ruleForm.schedule.type === "interval" ? "border-indigo-500 bg-indigo-50" : "border-slate-200"
                  }`}
                  onClick={() => setRuleForm({...ruleForm, schedule: {...ruleForm.schedule, type: "interval"}})}
                >
                  <Clock className="w-5 h-5 text-indigo-600 mb-2" />
                  <p className="font-medium">Periyodik</p>
                  <p className="text-xs text-slate-500">Belirli aralıklarla çalış</p>
                </div>
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    ruleForm.schedule.type === "cron" ? "border-indigo-500 bg-indigo-50" : "border-slate-200"
                  }`}
                  onClick={() => setRuleForm({...ruleForm, schedule: {...ruleForm.schedule, type: "cron"}})}
                >
                  <Target className="w-5 h-5 text-indigo-600 mb-2" />
                  <p className="font-medium">Zamanlanmış</p>
                  <p className="text-xs text-slate-500">Belirli zamanlarda çalış</p>
                </div>
              </div>
              
              {ruleForm.schedule.type === "interval" && (
                <div className="flex items-center gap-2">
                  <Label>Her</Label>
                  <Input
                    type="number"
                    value={ruleForm.schedule.intervalMinutes}
                    onChange={(e) => setRuleForm({
                      ...ruleForm, 
                      schedule: {...ruleForm.schedule, intervalMinutes: parseInt(e.target.value) || 60}
                    })}
                    className="w-[80px]"
                  />
                  <span className="text-slate-500">dakikada bir</span>
                </div>
              )}
            </div>

            {/* Durum */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Aktif</Label>
                <p className="text-xs text-slate-500">Kuralı etkinleştir</p>
              </div>
              <Switch
                checked={ruleForm.isActive}
                onCheckedChange={(v) => setRuleForm({...ruleForm, isActive: v})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button onClick={saveRule} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {editingRule ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
